import { defineStore, storeToRefs } from "pinia"
import { computed, ref, type Ref } from "vue"
import { useInstructionStore } from "@/store/instructions.store"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers"
import { RentalIntake } from "@/models/rental-intake.model"
import { useRelatedObjectsManager } from "@/composables/socket/related-objects-manager"
import type { Instruction } from "@/models/instruction.model"

export const useRentalIntakeStore = defineStore("rentalIntakeStore", () => {
  const instructionStore = useInstructionStore()
  const { instructions } = storeToRefs(instructionStore)

  const rentalIntakes = computed(() => {
    const rentalIntakes = []
    for (const instruction of instructions.value) {
      rentalIntakes.push(...instruction.rentalIntakes)
    }
    return rentalIntakes
  })

  // To prevent requesting the same search string multiple times
  // Note that is used for both rental intakes and rental dispenses (!)
  const retrievedSearchStrings: Ref<Set<string>> = ref(new Set())

  // All rental intake issuances that have been retrieved for the purpose of returning
  const retrievedRentalIntakes: Ref<{ [key: string]: RentalIntake }> = ref({})

  const returnableRentalIntakes = computed(() => {
    const combined = Object.values(retrievedRentalIntakes.value)
    for (const rentalIntake of rentalIntakes.value) {
      if (!retrievedRentalIntakes.value[rentalIntake.id]) {
        combined.push(rentalIntake)
      }
    }
    return combined.filter(
      (rentalIntake) => rentalIntake.isIssuance() && rentalIntake.getRemainingQuantity() > 0
    )
  })

  function addToReturnableRentalIntakes(newRentalIntakes: RentalIntake[]) {
    for (const rentalIntake of newRentalIntakes) {
      retrievedRentalIntakes.value[rentalIntake.id] = rentalIntake
    }
  }

  const { pushToRelatedObjects: pushRentalIntakeToRelatedObjects, removeFromRelatedObjects } =
    useRelatedObjectsManager(
      instructions,
      (model: RentalIntake) => model.instruction,
      (instruction: Instruction) => instruction.id!,
      (instruction: Instruction) => instruction.rentalIntakes
    )
  const { pushToRelatedObjects: pushRentalDispenseToRelatedObjects } = useRelatedObjectsManager(
    instructions,
    (model) => model?.instruction,
    (instruction: Instruction) => instruction.id!,
    (instruction: Instruction) => instruction.rentalDispenses
  )
  function pushRentalIntakeAndRentalDispenseToRelatedObjects(rentalIntake: RentalIntake) {
    pushRentalIntakeToRelatedObjects(rentalIntake)
    if (rentalIntake.associatedRentalDispense) {
      // Sometimes the socket received a new rental intake before the associated rental dispense. In that case, the rental dispense WILL
      // be created in the ModelManager (by RentalIntake childModel() call in constructor).
      // However, the subsequent rental dispense creation socket message will not be ignored, because the rental dispense already exists.
      // This also means that the rental dispense will not be added to the instruction.rentalDispenses array./
      // Therefore, we preemptively add the rental dispense to the related instruction here.
      pushRentalDispenseToRelatedObjects(rentalIntake.associatedRentalDispense)
    }
  }

  useSocketSynchronizationHandlers(
    RentalIntake,
    null,
    pushRentalIntakeAndRentalDispenseToRelatedObjects,
    removeFromRelatedObjects
  )
  return {
    rentalIntakes,
    returnableRentalIntakes,
    retrievedSearchStrings,
    pushRentalIntakeToRelatedObjects,
    removeFromRelatedObjects,
    addToReturnableRentalIntakes
  }
})
